import { MapIcon } from '@heroicons/react/16/solid';
import { MegaphoneIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { formatDateStr } from '../../../../../common-utils/formatters';
import { useAdsModal } from '../../../../hooks/useAdsModal';
import { DPEDict } from '../../../../utils/dictionnayMap';
import AdsModal from '../../../common/AdsModal/AdsModal';
import { Dpe } from '../../../common/Dpe';
import { Ges } from '../../../common/Ges';
import MUITable from '../../../common/MUITable';
import Popup from '../../../common/Popup';

interface DpeDetailsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  dpeDetails: Record<string, unknown>;
}

const formatValue = (key: string, value: unknown): string | React.ReactNode => {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }

  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return 'N/A';
  }

  const dict = DPEDict[key];

  // Handle date formatting
  if (dict?.type === 'date' && (typeof value === 'string' || value instanceof Date)) {
    try {
      return formatDateStr(value);
    } catch {
      return String(value);
    }
  }

  if (dict?.type === 'boolean') {
    return value === '1' || value === 1 || value === true ? 'Oui' : 'Non';
  }

  if (dict?.type === 'number' && (typeof value === 'string' || typeof value === 'number')) {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!isNaN(parsed)) {
      const numValue = Number.isInteger(parsed) ? parsed : Number(parsed.toFixed(2));
      return dict.unit ? `${numValue} ${dict.unit}` : String(numValue);
    }
  }

  // Special handling for DPE and GES classes
  if (key === 'classeBilanDpe') {
    return <Dpe dpe={String(value)} shape="rounded" />;
  }

  if (key === 'classeBilanGes') {
    return <Ges ges={String(value)} shape="rounded" />;
  }

  return String(value);
};

const DpeDetailsPopup: React.FC<DpeDetailsPopupProps> = ({ isOpen, onClose, dpeDetails }) => {
  const { handleAdsClickById, adsModalProps } = useAdsModal();

  const handleAdsClick = () => {
    const dpeId = dpeDetails?.remoteId || dpeDetails?.id;
    if (dpeId != null) {
      handleAdsClickById(dpeId as string | number);
    }
  };

  return (
    <>
      <Popup isOpen={isOpen} onClose={onClose} title="Détails du DPE">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            {dpeDetails?.banLabel && (
              <button
                onClick={() => {
                  const address = encodeURIComponent(String(dpeDetails.banLabel));
                  window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-600 px-3 py-1.5 font-medium text-indigo-600 transition-colors duration-200 hover:bg-indigo-600 hover:text-white"
              >
                <MapIcon className="h-5 w-5" />
                Voir sur Google Maps
              </button>
            )}
            <button
              onClick={handleAdsClick}
              className="inline-flex items-center gap-2 rounded-lg border border-green-600 px-3 py-1.5 font-medium text-green-600 transition-colors duration-200 hover:bg-green-600 hover:text-white"
            >
              <MegaphoneIcon className="h-5 w-5" />
              Recherche annonces
            </button>
          </div>
          <button
            onClick={() => window.open(`https://observatoire-dpe-audit.ademe.fr/afficher-dpe/${String(dpeDetails?.identifiantDpe ?? '')}`, '_blank', 'noopener,noreferrer')}
            className="rounded-lg border border-indigo-600 px-3 py-1.5 font-medium text-indigo-600 transition-colors duration-200 hover:bg-indigo-600 hover:text-white"
          >
            Ouvrir dans l'<span className="text-red-600">ADEME</span>
          </button>
        </div>

        <MUITable
          data={Object.entries(dpeDetails)
            .filter(([key]) => DPEDict[key])
            .sort(([keyA], [keyB]) => {
              const orderA = DPEDict[keyA]?.order ?? 999;
              const orderB = DPEDict[keyB]?.order ?? 999;
              return orderA - orderB;
            })
            .map(([key, value]) => ({
              Libellé: <strong>{DPEDict[key]?.label || key}</strong>,
              Valeur: formatValue(key, value),
            }))}
          headers={[
            { title: 'Libellé', class: 'w-1/2' },
            { title: 'Valeur', class: 'w-1/2' },
          ]}
          isSearchable={false}
          disablePagination
          tableClass="table-fixed"
        />
      </Popup>
      <AdsModal {...adsModalProps} />
    </>
  );
};

export default DpeDetailsPopup;
